import {
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  type User,
} from 'firebase/auth';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { track } from '../lib/analytics';
import { getAttribution, getTouches } from '../lib/attribution';
import { auth } from '../lib/firebase';

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null);
  const loading = ref(true);
  const error = ref<string | null>(null);
  const isImpersonating = ref(false);
  const impersonatedUserData = ref<{ uid: string; email: string; name: string } | null>(null);

  // Computed
  const isAuthenticated = computed(() => !!user.value);
  const emailVerified = computed(() => {
    if (impersonatedUserData.value) {
      return false; // Impersonated view doesn't show email verification
    }
    return user.value?.emailVerified ?? false;
  });
  const uid = computed(() => {
    // When impersonating, return the impersonated user's uid (from sessionStorage for immediate access)
    const impersonateUid = sessionStorage.getItem('impersonate');
    if (impersonateUid) {
      return impersonateUid;
    }
    return user.value?.uid ?? null;
  });
  const displayName = computed(() => {
    if (impersonatedUserData.value) {
      return impersonatedUserData.value.name;
    }
    return user.value?.displayName ?? user.value?.email ?? null;
  });
  const displayEmail = computed(() => {
    if (impersonatedUserData.value) {
      return impersonatedUserData.value.email;
    }
    return user.value?.email ?? null;
  });
  const impersonatingEmail = computed(() => (isImpersonating.value ? user.value?.email : null));
  const isAdmin = ref(false);

  // Check admin status from token
  async function checkAdminStatus() {
    if (!user.value) {
      isAdmin.value = false;
      return;
    }

    try {
      const { claims } = await user.value.getIdTokenResult();
      isAdmin.value = claims['admin'] === true;
    } catch {
      isAdmin.value = false;
    }
  }

  // Check for impersonation on init
  const checkImpersonation = async () => {
    // firebase.ts already handles URL param and sets sessionStorage
    const impersonateUid = sessionStorage.getItem('impersonate');
    isImpersonating.value = !!impersonateUid;

    // Load impersonated user data from API
    if (impersonateUid) {
      try {
        const token = await user.value?.getIdToken();
        if (!token) return;

        const response = await fetch(`/api/admin/users/${impersonateUid}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          impersonatedUserData.value = {
            uid: data.user.uid,
            email: data.user.email,
            name: data.user.name || data.user.email,
          };
        }
      } catch (err) {
        console.error('Failed to load impersonated user data:', err);
      }
    } else {
      impersonatedUserData.value = null;
    }
  };

  // Initialize auth state listener
  onAuthStateChanged(auth, async firebaseUser => {
    user.value = firebaseUser;
    loading.value = false;

    if (firebaseUser) {
      // Check admin status
      await checkAdminStatus();

      // Check for impersonation (needs to happen after user is set)
      await checkImpersonation();

      // Update last seen for authenticated users (skip when impersonating)
      if (!sessionStorage.getItem('impersonate')) {
        try {
          const token = await firebaseUser.getIdToken();
          await fetch(`/api/users/${firebaseUser.uid}/last-seen`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
        } catch (err) {
          console.error('Failed to update last seen:', err);
        }
      }
    } else {
      isAdmin.value = false;
      impersonatedUserData.value = null;
    }
  });

  // Get fresh ID token
  async function getIdToken(): Promise<string | null> {
    if (!user.value) return null;
    try {
      return await user.value.getIdToken();
    } catch (err) {
      console.error('Failed to get ID token:', err);
      return null;
    }
  }

  // Get auth headers including impersonation if active
  async function getAuthHeaders(): Promise<Record<string, string>> {
    const token = await getIdToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const impersonateUid = sessionStorage.getItem('impersonate');
    if (impersonateUid) {
      headers['x-impersonate'] = impersonateUid;
    }

    return headers;
  }

  // Sign in with email and password
  async function signInWithEmail(email: string, password: string): Promise<void> {
    error.value = null;
    try {
      await signInWithEmailAndPassword(auth, email, password);
      track('login', { method: 'password' });
    } catch (err: any) {
      console.error('Sign in error:', err);
      error.value = getErrorMessage(err.code);
      throw err;
    }
  }

  // Create or refresh the backend user row and Customer.io profile. The server treats the
  // request as the sign-up when isNewSignup is true, whichever request created the row first.
  // A failed PUT used to go unnoticed, so this checks the response and retries once.
  async function syncUserRecord(firebaseUser: User, isNewSignup: boolean): Promise<void> {
    const token = await firebaseUser.getIdToken();
    const body = JSON.stringify({
      name: firebaseUser.displayName || undefined,
      attribution: getAttribution(),
      touches: getTouches(),
      isNewSignup,
    });

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await fetch(`/api/users/${firebaseUser.uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body,
        });
        if (res.ok) return;
        console.error(`User record sync failed (${res.status})`, await res.text().catch(() => ''));
      } catch (err) {
        console.error('User record sync failed:', err);
      }
    }

    track('signup_sync_failed', { new_signup: isNewSignup });
  }

  // Sign up with email and password
  async function signUpWithEmail(email: string, password: string): Promise<void> {
    error.value = null;
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);

      await syncUserRecord(credential.user, true);

      track('sign_up', { method: 'password' });

      // Send verification email
      await sendEmailVerification(credential.user);
    } catch (err: any) {
      console.error('Sign up error:', err);
      error.value = getErrorMessage(err.code);
      throw err;
    }
  }

  // Sign in with Google
  async function signInWithGoogle(): Promise<void> {
    error.value = null;
    try {
      const provider = new GoogleAuthProvider();
      const credential = await signInWithPopup(auth, provider);

      // signInWithPopup covers both cases; only the first visit is a sign-up.
      const isNewUser = getAdditionalUserInfo(credential)?.isNewUser === true;
      track(isNewUser ? 'sign_up' : 'login', { method: 'google' });

      await syncUserRecord(credential.user, isNewUser);
    } catch (err: any) {
      console.error('Google sign in error:', err);
      error.value = getErrorMessage(err.code);
      throw err;
    }
  }

  // Sign out
  async function signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      console.error('Sign out error:', err);
      throw err;
    }
  }

  // Check if email exists
  async function checkEmailExists(email: string): Promise<boolean> {
    try {
      const response = await fetch(`/api/auth/check-email?email=${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        return data.exists;
      }
      return false;
    } catch (err) {
      console.error('Email check error:', err);
      return false;
    }
  }

  // Send password reset email
  async function resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err: any) {
      console.error('Password reset error:', err);
      error.value = getErrorMessage(err.code);
      throw err;
    }
  }

  // Resend verification email
  async function resendVerificationEmail(): Promise<void> {
    if (!user.value) {
      throw new Error('No user logged in');
    }

    try {
      await sendEmailVerification(user.value);
      const token = await user.value.getIdToken();
      await fetch(`/api/users/${user.value.uid}/resend-verification`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Resend verification error:', err);
      throw err;
    }
  }

  // Helper function to get user-friendly error messages
  function getErrorMessage(code: string): string {
    switch (code) {
      case 'auth/email-already-in-use':
        return "This email already has an account. If you deleted it, email support@twig.io and we'll restore it.";
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Try again later.';
      case 'auth/popup-closed-by-user':
        return 'Sign in cancelled.';
      default:
        return 'Authentication failed. Try again.';
    }
  }

  return {
    // State
    user,
    loading,
    error,
    isImpersonating,
    isAdmin,

    // Computed
    isAuthenticated,
    emailVerified,
    uid,
    displayName,
    displayEmail,
    impersonatingEmail,

    // Actions
    getIdToken,
    getAuthHeaders,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    checkEmailExists,
    resetPassword,
    resendVerificationEmail,
  };
});
